import { BaseFeature } from '../../core/base-feature.js';

/**
 * WriteLinesFeature - Frontend feature for Write Lines punishment
 * Allows admin to configure and trigger write lines sessions on desktop
 */
export class WriteLinesFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.sessionState = {
            isActive: false,
            text: '',
            linesRequired: 0,
            linesCompleted: 0,
            penaltyLinesAdded: 0,
            mistakeCount: 0,
            challengeModes: {
                blind: false,
                hiddenProgress: false,
                hiddenText: false,
                strict: 'off',
                noBackspace: false
            },
            startedAt: null,
            completedAt: null
        };

        // Event callbacks for React components
        this.stateUpdateCallbacks = new Set();
        this.completionCallbacks = new Set();
        this.updateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'write-lines',
            name: 'Write Lines',
            version: 'v1.0',
            tier: 'external',
            capabilities: [
                'start',
                'get_state',
                'cancel'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[WriteLines] Initializing...');
        console.log('[WriteLines] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId, fromId } = message;
        const command = cmd || action;

        switch (command) {
            // Real-time updates
            case 'state_update':
                return this.handleStateUpdate(payload);

            case 'completed':
            case 'timed_out':
                return this.handleCompleted(payload);

            case 'cancelled':
                return this.handleCancelled(payload);

            case 'initial_state':
                return this.handleInitialState(payload);

            // Responses to commands
            case 'start_response':
                return this.handleResponse('start', payload);

            case 'get_state_response':
                return this.handleResponse('get_state', payload);

            case 'cancel_response':
                return this.handleResponse('cancel', payload);

            case 'get_history_response':
                return this.handleResponse('get_history', payload);

            default:
                console.warn(`[WriteLines] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Real-time Event Handlers
    // =====================================

    handleStateUpdate(payload) {
        this.sessionState = {
            ...this.sessionState,
            isActive: true,
            linesCompleted: payload.linesCompleted ?? this.sessionState.linesCompleted,
            linesRequired: payload.linesRequired ?? this.sessionState.linesRequired,
            mistakeCount: payload.mistakeCount ?? this.sessionState.mistakeCount,
            penaltyLinesAdded: payload.penaltyLinesAdded ?? this.sessionState.penaltyLinesAdded,
            // Capture full state fields when present (e.g. first broadcast when AI starts a session)
            ...(payload.text != null && { text: payload.text }),
            ...(payload.challengeModes != null && { challengeModes: payload.challengeModes }),
            ...(payload.startedAt != null && { startedAt: payload.startedAt })
        };
        this.notifyStateUpdateListeners(payload);
        this.notifyUpdateListeners();
    }

    handleCompleted(payload) {
        console.log('[WriteLines] Session completed');
        this.sessionState = {
            ...this.sessionState,
            isActive: false,
            linesCompleted: payload.linesCompleted ?? this.sessionState.linesCompleted,
            completedAt: payload.completedAt || Date.now()
        };
        this.notifyCompletionListeners(payload);
        this.notifyUpdateListeners();
    }

    handleCancelled(payload) {
        console.log('[WriteLines] Session cancelled');
        this.sessionState = {
            ...this.sessionState,
            isActive: false,
            completedAt: Date.now()
        };
        this.notifyCompletionListeners({ ...payload, type: 'cancelled' });
        this.notifyUpdateListeners();
    }

    handleInitialState(payload) {
        console.log('[WriteLines] Received initial state');
        if (payload.state) {
            this.sessionState = payload.state;
        }
        this.notifyUpdateListeners();
    }

    // =====================================
    // Generic Response Handler
    // =====================================

    handleResponse(requestType, payload) {
        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // Command Methods
    // =====================================

    /**
     * Start a write lines session
     * @param {Object} config - { text, lineCount, challengeModes }
     * @returns {Promise<Object>}
     */
    async start(config) {
        return new Promise((resolve) => {
            this.sendMessage('start', config);
            this._pendingRequests.set('start', resolve);
        });
    }

    /**
     * Get current state
     * @returns {Promise<Object>}
     */
    async getSessionState() {
        return new Promise((resolve) => {
            this.sendMessage('get_state', {});
            this._pendingRequests.set('get_state', resolve);
        });
    }

    /**
     * Cancel current session
     * @returns {Promise<Object>}
     */
    async cancel() {
        return new Promise((resolve) => {
            this.sendMessage('cancel', {});
            this._pendingRequests.set('cancel', resolve);
        });
    }

    /**
     * Get session history from Sacred Timeline
     * @param {number} limit - Number of sessions to fetch
     * @param {number} offset - Offset for pagination
     * @returns {Promise<Object>} { sessions, total }
     */
    async getHistory(limit = 10, offset = 0) {
        return new Promise((resolve) => {
            this.sendMessage('get_history', { limit, offset });
            this._pendingRequests.set('get_history', resolve);
        });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    /**
     * Subscribe to state updates (progress)
     */
    onStateUpdate(callback) {
        this.stateUpdateCallbacks.add(callback);
        return () => this.stateUpdateCallbacks.delete(callback);
    }

    /**
     * Subscribe to completion events
     */
    onCompletion(callback) {
        this.completionCallbacks.add(callback);
        return () => this.completionCallbacks.delete(callback);
    }

    /**
     * Subscribe to general updates
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyStateUpdateListeners(data) {
        for (const callback of this.stateUpdateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[WriteLines] Error in state update callback:', error);
            }
        }
    }

    notifyCompletionListeners(data) {
        for (const callback of this.completionCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[WriteLines] Error in completion callback:', error);
            }
        }
    }

    notifyUpdateListeners() {
        const data = {
            state: this.sessionState
        };
        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[WriteLines] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getState() {
        return this.sessionState;
    }

    isActive() {
        return this.sessionState.isActive;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[WriteLines] Cleaning up...');
        this.sessionState = {
            isActive: false,
            text: '',
            linesRequired: 0,
            linesCompleted: 0,
            penaltyLinesAdded: 0,
            mistakeCount: 0,
            challengeModes: { blind: false, hiddenProgress: false, hiddenText: false, strict: 'off', noBackspace: false },
            startedAt: null,
            completedAt: null
        };
        this.stateUpdateCallbacks.clear();
        this.completionCallbacks.clear();
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
